"use strict";
/* globals module */
/**
 * @module articleRoute
 * @description Baasic Article Route Definition provides Baasic route templates which can be expanded to Baasic REST URIs. Various services can use Baasic Article Route Service to obtain needed routes while other routes will be obtained through HAL. By convention, all route services use the same function names as their corresponding services.
*/
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var inversify_1 = require("inversify");
var common_1 = require("../../common");
;
var contracts_1 = require("../../core/contracts");
var _1 = require("./");
var ArticleRoute = /** @class */ (function (_super) {
    tslib_1.__extends(ArticleRoute, _super);
    function ArticleRoute(articleSubscriptionsRoute, articleInstanceCommentsRoute, articleInstanceFilesRoute, articleInstanceRatingsRoute, articleInstanceTagsRoute, articleACLRoute, appOptions) {
        var _this = _super.call(this, appOptions) || this;
        _this.articleSubscriptionsRoute = articleSubscriptionsRoute;
        _this.articleInstanceCommentsRoute = articleInstanceCommentsRoute;
        _this.articleInstanceFilesRoute = articleInstanceFilesRoute;
        _this.articleInstanceRatingsRoute = articleInstanceRatingsRoute;
        _this.articleInstanceTagsRoute = articleInstanceTagsRoute;
        _this.articleACLRoute = articleACLRoute;
        _this.appOptions = appOptions;
        _this.findRoute = 'articles/{?searchQuery,page,rpp,sort,embed,fields,statuses,tags,startDate,endDate}';
        _this.getRoute = 'articles/{id}/{?embed,fields}';
        _this.publishRoute = 'articles/{id}/publish/';
        _this.purgeRoute = 'articles/purge/';
        _this.createRoute = 'articles';
        _this.updateRoute = 'articles/{id}';
        _this.deleteRoute = 'articles/{id}';
        _this.archiveRoute = 'articles/{id}/archive';
        _this.restoreRoute = 'articles/{id}/restore';
        _this.unpublishRoute = 'articles/{id}/unpublish';
        return _this;
    }
    Object.defineProperty(ArticleRoute.prototype, "subscriptions", {
        get: function () {
            return this.articleSubscriptionsRoute;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ArticleRoute.prototype, "comments", {
        get: function () {
            return this.articleInstanceCommentsRoute;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ArticleRoute.prototype, "files", {
        get: function () {
            return this.articleInstanceFilesRoute;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ArticleRoute.prototype, "ratings", {
        get: function () {
            return this.articleInstanceRatingsRoute;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ArticleRoute.prototype, "tags", {
        get: function () {
            return this.articleInstanceTagsRoute;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ArticleRoute.prototype, "acl", {
        get: function () {
            return this.articleACLRoute;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Parses find article route which can be expanded with additional options. Supported items are:
     * - `searchQuery` - A string referencing article properties using the phrase or BQL (Baasic Query Language) search.
     * - `page` - A value used to set the page number, i.e. to retrieve certain article subset from the storage.
     * - `rpp` - A value used to limit the size of result set per page.
     * - `sort` - A string used to set the article property to sort the result collection by.
     * - `embed` - Comma separated list of resources to be contained within the current representation.
     * - `startDate` - A value used to specify the article creation, publish or archive date date starting from which article resource collection should be returned.
     * - `endDate` - A value used to specify the article creation, publish or archive date until (and including) which article resource collection should be returned.
     * - `statuses` - Comma separated list of article statuses that specify where search should be done (Allowed statuses: Published, Draft and Archived).
     * - `tags` - A value used to restrict the search to article resources with these tags. Multiple tags should be comma separated.
     * @method
     * @param options A promise that is resolved once the find action has been performed.
     * @example articleRoute.find.expand({searchQuery: '<search-phrase>'});
     **/
    ArticleRoute.prototype.find = function (options) {
        var opt = options || {};
        opt.startDate = this.getStartDate(opt);
        opt.endDate = this.getEndDate(opt);
        return _super.prototype.baseFind.call(this, this.findRoute, opt);
    };
    /**
     * Parses get article route which must be expanded with the Id of the previously created article resource in the system. Additional expand supported items are:
     * - `embed` - Comma separated list of resources to be contained within the current representation.
     * @method
     * @param id Article slug or id which uniquely identifies article resource that needs to be retrieved.
     * @param options Options object that contains embed items.
     * @example articleRoute.get({id: '<article-id>'});
     **/
    ArticleRoute.prototype.get = function (id, options) {
        return _super.prototype.baseGet.call(this, this.getRoute, id, options);
    };
    /**
     * Parses publish article route which must be expanded with the Id of the previously created article resource in the system.
     * @method
     * @param data An article object.
     * @example articleRoute.publish.expand({id: '<article-id>'});
     **/
    ArticleRoute.prototype.publish = function (data) {
        return _super.prototype.baseGet.call(this, this.publishRoute, data);
    };
    /**
     * Parses purge article route, this URI template doesn't expose any additional properties.
     * @method
     * @example articleRoute.purge();
     **/
    ArticleRoute.prototype.purge = function (options) {
        return _super.prototype.baseCreate.call(this, this.purgeRoute, options);
    };
    /**
     * Parses create article route; this URI template doesn't expose any additional properties.
     * @method
     * @example articleRoute.create();
     **/
    ArticleRoute.prototype.create = function () {
        return _super.prototype.baseCreate.call(this, this.createRoute, {});
    };
    /**
     * Parses update article route; this URI template doesn't expose any additional properties.
     * @method
     * @param data An article object that needs to be updated into the system.
     * @example articleRoute.update(data);
     **/
    ArticleRoute.prototype.update = function (data) {
        return _super.prototype.baseUpdate.call(this, this.updateRoute, data);
    };
    /**
     * Parses delete article route; this URI template doesn't expose any additional properties.
     * @method
     * @param data An article object that needs to be removed from the system.
     * @example articleRoute.delete(data);
     **/
    ArticleRoute.prototype.delete = function (data) {
        return _super.prototype.baseDelete.call(this, this.deleteRoute, data);
    };
    /**
     * Parses archive article route; this URI template doesn't expose any additional properties.
     * @method
     * @param data An article object.
     * @example articleRoute.archive(data);
     **/
    ArticleRoute.prototype.archive = function (data) {
        return _super.prototype.baseUpdate.call(this, this.archiveRoute, data, undefined, 'archive');
    };
    /**
     * Parses restore article route; this URI template doesn't expose any additional properties.
     * @method
     * @example articleRoute.restore(data);
     **/
    ArticleRoute.prototype.restore = function (data) {
        return _super.prototype.baseUpdate.call(this, this.restoreRoute, data, undefined, 'restore');
    };
    /**
     * Returns a promise that is resolved once the unpublish article action has been performed. This action sets the status of an article from "published" to "draft". This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(article);
     * let uri = params['model'].links('unpublish').href;
     * ```
     * @method
     * @example // article is a resource previously fetched using get action.
                    articleClient.unpublish(article)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    ArticleRoute.prototype.unpublish = function (data) {
        return _super.prototype.baseUpdate.call(this, this.unpublishRoute, data, undefined, 'unpublish');
    };
    ArticleRoute.prototype.getStartDate = function (options) {
        if (!this.utility.isUndefined(options.startDate) && options.startDate !== null) {
            return options.startDate.toISOString();
        }
        return undefined;
    };
    ArticleRoute.prototype.getEndDate = function (options) {
        if (!this.utility.isUndefined(options.endDate) && options.endDate !== null) {
            return options.endDate.toISOString();
        }
        return undefined;
    };
    var _a, _b, _c, _d, _e, _f, _g;
    ArticleRoute = tslib_1.__decorate([
        inversify_1.injectable(),
        tslib_1.__param(0, inversify_1.inject(_1.TYPES.ArticleSubscriptionsRoute)),
        tslib_1.__param(1, inversify_1.inject(_1.TYPES.ArticleInstanceCommentsRoute)),
        tslib_1.__param(2, inversify_1.inject(_1.TYPES.ArticleInstanceFilesRoute)),
        tslib_1.__param(3, inversify_1.inject(_1.TYPES.ArticleInstanceRatingsRoute)),
        tslib_1.__param(4, inversify_1.inject(_1.TYPES.ArticleInstanceTagsRoute)),
        tslib_1.__param(5, inversify_1.inject(_1.TYPES.ArticleACLRoute)),
        tslib_1.__param(6, inversify_1.inject(contracts_1.TYPES.IAppOptions)),
        tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof _1.ArticleSubscriptionsRoute !== "undefined" && _1.ArticleSubscriptionsRoute) === "function" ? _a : Object, typeof (_b = typeof _1.ArticleInstanceCommentsRoute !== "undefined" && _1.ArticleInstanceCommentsRoute) === "function" ? _b : Object, typeof (_c = typeof _1.ArticleInstanceFilesRoute !== "undefined" && _1.ArticleInstanceFilesRoute) === "function" ? _c : Object, typeof (_d = typeof _1.ArticleInstanceRatingsRoute !== "undefined" && _1.ArticleInstanceRatingsRoute) === "function" ? _d : Object, typeof (_e = typeof _1.ArticleInstanceTagsRoute !== "undefined" && _1.ArticleInstanceTagsRoute) === "function" ? _e : Object, typeof (_f = typeof _1.ArticleACLRoute !== "undefined" && _1.ArticleACLRoute) === "function" ? _f : Object, typeof (_g = typeof contracts_1.IAppOptions !== "undefined" && contracts_1.IAppOptions) === "function" ? _g : Object])
    ], ArticleRoute);
    return ArticleRoute;
}(common_1.BaseRoute));
exports.ArticleRoute = ArticleRoute;
/**
 * @copyright (c) 2017 Mono Ltd
 * @license MIT
 * @author Mono Ltd
 * @overview
 ***Notes:**
 - Refer to the [Baasic REST API](http://dev.baasic.com/api/reference/home) for detailed information about available Baasic REST API end-points.
 - [URI Template](https://github.com/Baasic/uritemplate-js) syntax enables expanding the Baasic route templates to Baasic REST URIs providing it with an object that contains URI parameters.
 - All end-point objects are transformed by the associated route service.
*/
